import { useEffect, useMemo, useState } from 'react';
import { Alert, ScrollView, Text, View } from 'react-native';
import { trpc } from '../lib/trpc';
import { BottomSheet } from './BottomSheet';
import { Button } from './Button';
import { CurrencyInput } from './CurrencyInput';
import { FormErrorBanner } from './FormErrorBanner';
import { Select } from './Select';
import { TextInput } from './TextInput';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { ACCOUNT_TAX_SUBTYPE_OPTIONS } from '../lib/accountTaxSubtypes';

type AccountType = 'depository' | 'investment' | 'credit' | 'loan' | 'other';

const ACCOUNT_TYPE_OPTIONS: Array<{ value: AccountType; label: string }> = [
  { value: 'depository', label: 'Cash (checking, savings, HSA, money market)' },
  { value: 'investment', label: 'Investment (brokerage, IRA, 401k, 529)' },
  { value: 'credit', label: 'Credit card' },
  { value: 'loan', label: 'Loan (mortgage, student, auto)' },
  { value: 'other', label: 'Other' },
];

const DEPOSITORY_SUBTYPES = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'cd', label: 'CD' },
  { value: 'money market', label: 'Money market' },
  { value: 'hsa', label: 'HSA' },
  { value: 'cash management', label: 'Cash management' },
  { value: 'other', label: 'Other / unknown' },
];

const CREDIT_SUBTYPES = [
  { value: 'credit card', label: 'Credit card' },
  { value: 'other', label: 'Other / unknown' },
];

const LOAN_SUBTYPES = [
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'student', label: 'Student loan' },
  { value: 'auto', label: 'Auto loan' },
  { value: 'personal', label: 'Personal loan' },
  { value: 'other', label: 'Other / unknown' },
];

const OTHER_SUBTYPES = [{ value: 'other', label: 'Other / unknown' }];

function subtypeOptionsForType(type: AccountType) {
  switch (type) {
    case 'depository':
      return DEPOSITORY_SUBTYPES;
    case 'investment':
      // Investment subtypes drive tax bucketing, so reuse the canonical list.
      return ACCOUNT_TAX_SUBTYPE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }));
    case 'credit':
      return CREDIT_SUBTYPES;
    case 'loan':
      return LOAN_SUBTYPES;
    case 'other':
      return OTHER_SUBTYPES;
  }
}

interface AddManualAccountSheetProps {
  visible: boolean;
  onClose: () => void;
  /** Existing manual connection to add an account under. If null, the sheet collects an institution name first. */
  manualConnectionId: string | null;
  onCreated?: (params: { connectionId: string; accountId: string }) => void;
}

export function AddManualAccountSheet({
  visible,
  onClose,
  manualConnectionId,
  onCreated,
}: AddManualAccountSheetProps) {
  const isDesktop = useIsDesktop();
  const utils = trpc.useUtils();
  const createConnection = trpc.connections.createManual.useMutation();
  const createAccount = trpc.accounts.createManual.useMutation();

  const [institutionName, setInstitutionName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [type, setType] = useState<AccountType>('depository');
  const [subtype, setSubtype] = useState<string>('checking');
  const [balance, setBalance] = useState('');
  const [error, setError] = useState<string | null>(null);

  const subtypeOptions = useMemo(() => subtypeOptionsForType(type), [type]);

  useEffect(() => {
    if (!visible) return;
    setInstitutionName('');
    setAccountName('');
    setType('depository');
    setSubtype('checking');
    setBalance('');
    setError(null);
  }, [visible]);

  useEffect(() => {
    // Reset subtype when type changes so we never show an inapplicable value.
    const first = subtypeOptionsForType(type)[0];
    if (first) setSubtype(first.value);
  }, [type]);

  const isPending = createConnection.isPending || createAccount.isPending;
  const requiresInstitution = manualConnectionId == null;

  const handleSubmit = async () => {
    setError(null);

    if (requiresInstitution && institutionName.trim().length === 0) {
      setError('Enter the institution name (e.g. Optum Bank).');
      return;
    }
    if (accountName.trim().length === 0) {
      setError('Enter an account name.');
      return;
    }
    const parsed = Number(balance);
    if (!Number.isFinite(parsed)) {
      setError('Enter a valid balance.');
      return;
    }

    try {
      let connectionId = manualConnectionId;
      if (!connectionId) {
        const result = await createConnection.mutateAsync({
          institutionName: institutionName.trim(),
        });
        connectionId = result.connectionId;
      }

      const account = await createAccount.mutateAsync({
        connectionId,
        name: accountName.trim(),
        type,
        subtype,
        balance: parsed,
      });

      await Promise.allSettled([
        utils.connections.list.invalidate(),
        utils.accounts.list.invalidate(),
        utils.fire.getRecalcStatus.invalidate(),
      ]);

      onCreated?.({ connectionId, accountId: account.id });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save account.';
      setError(message);
    }
  };

  return (
    <BottomSheet
      visible={visible}
      onClose={onClose}
      title="Add account manually"
      variant={isDesktop ? 'centered' : 'bottom'}
    >
      <ScrollView className="px-5 py-4">
        <Text className="text-sm text-gray-600 dark:text-gray-300">
          Use this for institutions our connected providers don't support (HSA, 529, foreign banks, crypto wallets).
          You'll update the balance yourself — we won't see transactions for these accounts.
        </Text>

        {error && (
          <View className="mt-4">
            <FormErrorBanner error={error} />
          </View>
        )}

        {requiresInstitution && (
          <TextInput
            containerClassName="mt-4"
            label="Institution"
            placeholder="e.g. Optum Bank"
            value={institutionName}
            onChangeText={setInstitutionName}
            autoCapitalize="words"
            editable={!isPending}
          />
        )}

        <TextInput
          containerClassName="mt-4"
          label="Account name"
          placeholder="e.g. HSA"
          value={accountName}
          onChangeText={setAccountName}
          autoCapitalize="words"
          editable={!isPending}
        />

        <Select
          className="mt-4"
          label="Account type"
          modalTitle="Account type"
          value={type}
          options={ACCOUNT_TYPE_OPTIONS}
          onChange={(value) => setType(value as AccountType)}
          disabled={isPending}
        />

        <Select
          className="mt-4"
          label="Subtype"
          modalTitle="Subtype"
          value={subtype}
          options={subtypeOptions}
          onChange={setSubtype}
          disabled={isPending}
        />

        <CurrencyInput
          containerClassName="mt-4"
          label="Current balance"
          placeholder="0.00"
          value={balance}
          onChangeText={setBalance}
          editable={!isPending}
        />

        <View className="mt-5 gap-3 pb-4">
          <Button onPress={handleSubmit} loading={isPending}>
            Add account
          </Button>
          <Button variant="secondary" onPress={onClose} disabled={isPending}>
            Cancel
          </Button>
        </View>
      </ScrollView>
    </BottomSheet>
  );
}
